/*
 * Onboarding 002 — DOM helpers, chrome and the config-driven screens.
 *
 * All measurements are transcribed from the design files (Reformly Quiz Funnel
 * Web/Mobile.dc.html). The two breakpoints are frequently DIFFERENT LAYOUTS rather than
 * one layout reflowing, so where they diverge both are written out and switched with CSS
 * rather than being averaged into one responsive rule.
 *
 * Loaded after quiz-002-data.js.
 */
window.ReformlyQuiz = window.ReformlyQuiz || {};

(function (NS) {
  'use strict';

  /* ─── DOM helpers ────────────────────────────────────────────────────────── */

  /** el('div.o2-card', { onclick: fn }, [children]) */
  function el(spec, attrs, children) {
    var parts = String(spec).split('.');
    var node = document.createElement(parts.shift() || 'div');
    if (parts.length) node.className = parts.join(' ');
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        var v = attrs[k];
        if (v == null || v === false) return;
        if (k === 'html') node.innerHTML = v;
        else if (k === 'text') node.textContent = v;
        else if (k.slice(0, 2) === 'on') node.addEventListener(k.slice(2), v);
        else if (k === 'style') Object.assign(node.style, v);
        else node.setAttribute(k, v === true ? '' : v);
      });
    }
    (Array.isArray(children) ? children : children ? [children] : []).forEach(function (c) {
      if (c == null || c === false) return;
      node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return node;
  }

  function svg(markup) {
    var wrap = document.createElement('div');
    wrap.innerHTML = markup.trim();
    return wrap.firstChild;
  }

  /** Copy can be a string or a function of state — W06 and W14 personalise theirs. */
  function resolve(v) { return typeof v === 'function' ? v() : v; }

  /* ─── Icons, traced from the design markup ───────────────────────────────── */

  var ICON = {
    back: '<svg width="9" height="16" viewBox="0 0 9 16" fill="none"><path d="M8 1L1 8L8 15" stroke="#222" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    // 11×9 on standard cards, 10×8 on compact. Stroke is #FAF9F5 (surface), not white.
    tick: '<svg viewBox="0 0 11 9" fill="none" class="o2-tick"><path d="M1 4.6L4 7.4L10 1.5" stroke="#FAF9F5" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    arrow: '<svg width="9" height="9" viewBox="0 0 9 9" fill="none"><path d="M1 4.5H8M5 1.5L8 4.5L5 7.5" stroke="#222" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    checkGreen: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8" stroke="#00B67A" stroke-width="1.5"/><path d="M6.5 10.2L9 12.6L13.6 7.4" stroke="#00B67A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    crossMuted: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8" stroke="#6F6659" stroke-width="1.5"/><path d="M7.2 7.2l5.6 5.6M12.8 7.2l-5.6 5.6" stroke="#6F6659" stroke-width="1.5" stroke-linecap="round"/></svg>',
    star: '<svg width="14" height="14" viewBox="0 0 14 14" fill="#00B67A"><path d="M7 1l1.8 3.9 4.2.5-3.1 2.9.8 4.2L7 10.6 3.3 12.5l.8-4.2L1 5.4l4.2-.5z"/></svg>',
    tickGreenSmall: '<svg width="12" height="10" viewBox="0 0 11 9" fill="none"><path d="M1 4.6L4 7.4L10 1.5" stroke="#00B67A" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  };

  /* W07 option icons — 18×18, muted, stroke-width 1.4. Mobile only: the web markup for
     this screen has no icons at all, its cards are the same plain boxes as everywhere. */
  var OPT_ICON = {
    health: '<circle cx="9" cy="9" r="6.4"/><path d="M9 6v6M6 9h6"/>',
    confidence: '<path d="M9 15V4M4.5 8.5L9 4l4.5 4.5"/>',
    fit: '<path d="M2 9h5M4.6 6.4L7.2 9l-2.6 2.6M16 9h-5M13.4 6.4L10.8 9l2.6 2.6"/>',
    energy: '<path d="M10.2 2L4.6 10h4l-.8 6 5.6-8h-4l.8-6Z"/>',
    posture: '<circle cx="9" cy="4.6" r="2.4"/><path d="M4.8 15.4v-2.8a4.2 4.2 0 018.4 0v2.8"/>',
    calm: '<circle cx="9" cy="9" r="6.4"/><circle cx="9" cy="9" r="2.6"/>',
  };

  function optIcon(name) {
    return svg('<svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#6F6659" ' +
      'stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">' + (OPT_ICON[name] || '') + '</svg>');
  }

  /* ─── Chrome ─────────────────────────────────────────────────────────────── */

  /**
   * Header. 56px mobile / 72px web, hairline rule beneath.
   * Rendered once by the wizard and kept OUTSIDE the screen transition: it is identical on
   * every screen, so fading it would mean two identical headers crossfading over each
   * other, and partial opacity over partial opacity does not sum to full.
   */
  function header(opts) {
    var back = opts.onBack
      ? el('button.o2-back', { type: 'button', 'aria-label': 'Back', onclick: opts.onBack }, svg(ICON.back))
      : el('span.o2-back-spacer');

    var mark = el('img.o2-wordmark', { src: NS.asset('wordmark'), alt: 'Reformly' });

    /*
     * Mobile centres the wordmark with the back arrow to its left; web puts the two
     * together on the left and centres the progress bar between them and the right spacer.
     * They are grouped here so the web layout can left-align both without a second DOM.
     */
    return el('header.o2-header', null, [
      el('div.o2-header-row', null, [
        el('div.o2-header-left', null, [back, mark]),
        el('span.o2-back-spacer'),
      ]),
      opts.part ? progress(opts.part, opts.label) : null,
    ]);
  }

  /** Five segments, one per part. The just-completed one animates its fill. */
  function progress(part, label) {
    var idx = NS.PARTS.indexOf(part);
    var bar = el('div.o2-progress', { role: 'progressbar', 'aria-valuenow': idx + 1, 'aria-valuemin': 1, 'aria-valuemax': NS.PARTS.length });
    NS.PARTS.forEach(function (p, i) {
      var seg = el('span.o2-seg');
      var fill = el('span.o2-seg-fill');
      if (i <= idx) fill.classList.add('is-on');
      // Delay only the segment that just completed, so it reads as reacting to the answer.
      if (i === idx) fill.style.transitionDelay = '120ms';
      seg.appendChild(fill);
      bar.appendChild(seg);
    });
    return el('div.o2-progress-wrap', null, [el('span.o2-eyebrow', { text: label }), bar]);
  }

  function primaryButton(text, onClick, opts) {
    opts = opts || {};
    var b = el('button.o2-btn', { type: 'button', onclick: onClick }, text);
    if (opts.disabled) b.disabled = true;
    if (opts.radius === 10) b.classList.add('r10');
    return b;
  }

  function textButton(text, onClick) {
    return el('button.o2-textbtn', { type: 'button', onclick: onClick }, text);
  }

  /**
   * Real photography, where a screen has it.
   *
   * <picture> rather than two <img> toggled by CSS: the browser resolves the media query
   * before fetching, so only the chosen file is downloaded. Two images with .only-mobile /
   * .only-web would pull both on every device.
   */
  function photoImage(keys) {
    var pic = el('picture');
    if (keys.w) {
      var web = photoSrcset(NS.asset(keys.w), true);
      pic.appendChild(el('source', { media: '(min-width: 768px)', srcset: web.srcset, sizes: web.sizes }));
    }
    var url = NS.asset(keys.m || keys.w);
    var mob = photoSrcset(url, false);
    pic.appendChild(el('img', {
      src: NS.sized(url, 800),
      srcset: mob.srcset,
      sizes: mob.sizes,
      alt: '',
      // Above the fold on every screen it appears on, so never lazy — but decoding off the
      // main thread keeps it from delaying the step transition.
      decoding: 'async',
      fetchpriority: 'high',
    }));
    return pic;
  }

  /**
   * Shopify CDN candidates at several widths so the browser picks by DPR and viewport.
   *
   * Shared with the prefetcher in quiz-002.js: it warms images by setting the SAME srcset
   * and sizes on a detached Image, so the browser resolves to the same candidate and the
   * real <img> is a cache hit. Warming a single fixed width instead just downloads a file
   * nothing goes on to request.
   */
  function photoSrcset(url, wide) {
    var widths = wide ? [600, 900, 1200] : [400, 600, 800];
    return {
      srcset: widths.map(function (w) { return NS.sized(url, w) + ' ' + w + 'w'; }).join(', '),
      sizes: wide ? '50vw' : '100vw',
    };
  }

  /** Placeholder for photography that does not exist yet — renders the brief so the shoot
      list is visible in the running funnel rather than being 20 grey boxes. */
  function photoSlot(brief, height) {
    return el('div.o2-photo', { role: 'img', 'aria-label': brief, style: height ? { height: height + 'px' } : null },
      el('span', { text: brief }));
  }

  /**
   * Page shell for question screens: content column, optional title block, footer.
   * Web column is 560px with 72/48/64 padding; mobile is full width inside 20px.
   */
  function screen(opts) {
    var col = el('div.o2-col' + (opts.wide ? '.wide' : ''));

    if (opts.title || opts.subtitle) {
      var titles = el('div.o2-titles' + (opts.subtitle ? '.has-sub' : ''));
      if (opts.title) titles.appendChild(el('h1.o2-title' + (opts.interstitial ? '.big' : ''), { text: resolve(opts.title) }));
      if (opts.subtitle) titles.appendChild(el('p.o2-sub', { text: resolve(opts.subtitle) }));
      col.appendChild(titles);
    }

    var body = el('div.o2-body');
    if (opts.gap != null) body.style.setProperty('--o2-gap', opts.gap + 'px');
    if (opts.gapMd != null) body.style.setProperty('--o2-gap-md', opts.gapMd + 'px');
    (Array.isArray(opts.content) ? opts.content : [opts.content]).forEach(function (c) {
      if (c) body.appendChild(c);
    });
    col.appendChild(body);

    if (opts.footer) col.appendChild(el('div.o2-footer', null, opts.footer));

    return el('main.o2-main', null, col);
  }

  /* ─── Answer cards ───────────────────────────────────────────────────────── */

  /**
   * Selected does NOT invert the card. It fills with #EFE9DE, keeps the hairline border
   * and the ink label, and fills only the indicator. ("Same inversion" in the design's
   * style sheet refers to the indicator, not the card.)
   *
   * variant: plain 60/p18, icon 56/p8-16, compact 48/p10-14. A card with a sublabel is
   * taller again (74/p14-18) regardless of variant.
   */
  function optionCard(o, opts) {
    var selected = opts.selected;
    var cls = 'button.o2-card.' + (opts.variant || 'plain');
    if (o.sub) cls += '.has-sub';
    if (selected) cls += '.is-on';

    var kids = [];
    if (opts.variant === 'icon' && o.icon) {
      kids.push(el('span.o2-card-icon', null, optIcon(o.icon)));
    }

    var labels = [el('span.o2-card-label', { text: o.l })];
    if (o.sub) labels.push(el('span.o2-card-sub', { text: o.sub }));
    kids.push(el('span.o2-card-text', null, labels));

    var ind = el('span.o2-ind' + (opts.multi ? '.square' : ''));
    if (selected) ind.appendChild(svg(ICON.tick));
    kids.push(ind);

    var node = el(cls, { type: 'button', 'aria-pressed': selected ? 'true' : 'false', onclick: opts.onClick }, kids);
    node._ind = ind;
    return node;
  }

  /**
   * Flips a card's selected state IN PLACE.
   *
   * Rebuilding the screen on every tap — which is what a naive port of React's re-render
   * does — destroys and recreates every node on the page. That restarts the screen's
   * enter animation, drops focus, and reads as the whole page flickering. Only the two
   * cards whose state actually changed need to be touched.
   */
  function setCardSelected(node, selected) {
    if (!node || node.classList.contains('is-on') === selected) return;
    node.classList.toggle('is-on', selected);
    node.setAttribute('aria-pressed', selected ? 'true' : 'false');
    var ind = node._ind;
    if (!ind) return;

    if (selected) {
      ind.innerHTML = '';
      ind.appendChild(svg(ICON.tick));
      return;
    }

    // Let the check animate out rather than disappearing on the frame — the React version
    // had an AnimatePresence exit. Removed once the 140ms animation has run.
    var tick = ind.firstChild;
    if (!tick) return;
    tick.classList.add('is-out');
    setTimeout(function () { if (tick.parentNode === ind) ind.removeChild(tick); }, 150);
  }

  /* ─── Config-driven screens ──────────────────────────────────────────────── */

  /**
   * Tapping an answer stores it and advances after a short hold. Without the hold the
   * screen changes on the same frame as the tap, the selection is never seen, and it
   * reads as "did that register?".
   */
  var SETTLE_MS = 180;

  function renderSingle(step, ctx) {
    var current = step.get();
    var list = el('div.o2-list' + (step.variant && step.variant !== 'plain' ? '.tight' : ''));
    var nodes = {};

    step.options.forEach(function (o) {
      var node = optionCard(o, {
        variant: step.variant, selected: current === o.v,
        onClick: function () {
          var prev = step.get();
          step.set(o.v);
          // In place: clear the old card, light the new one. No screen rebuild.
          if (prev && nodes[prev]) setCardSelected(nodes[prev], false);
          setCardSelected(node, true);
          setTimeout(ctx.next, SETTLE_MS);
        },
      });
      nodes[o.v] = node;
      list.appendChild(node);
    });

    return screen({
      title: step.title, subtitle: step.subtitle,
      gap: step.contentGap != null ? step.contentGap : 28,
      content: list,
    });
  }

  function renderMulti(step, ctx) {
    var current = (step.get() || []).slice();
    var list = el('div.o2-list' + (step.variant && step.variant !== 'plain' ? '.tight' : ''));
    var nodes = {};
    var cta = null;

    step.options.forEach(function (o) {
      var node = optionCard(o, {
        variant: step.variant, multi: true, selected: current.indexOf(o.v) > -1,
        onClick: function () {
          if (o.v === step.exclusive) {
            // "None of these" clears everything else, and any other pick clears it.
            current = current.indexOf(o.v) > -1 ? [] : [o.v];
          } else {
            current = current.indexOf(o.v) > -1
              ? current.filter(function (x) { return x !== o.v; })
              : current.concat([o.v]);
            if (step.exclusive) current = current.filter(function (x) { return x !== step.exclusive; });
          }
          step.set(current);
          // Repaint only the cards, not the screen.
          step.options.forEach(function (opt) {
            setCardSelected(nodes[opt.v], current.indexOf(opt.v) > -1);
          });
          if (cta) cta.disabled = current.length === 0;
        },
      });
      nodes[o.v] = node;
      list.appendChild(node);
    });

    var content = list;
    if (step.aside === 'coach') {
      content = el('div.o2-aside-row', null, [
        // Sized by HEIGHT in CSS (352 mobile / 430 web) in a 126-180px column, so the widest
        // it ever paints is ~215px at 1x. 250/500 covers 2x without the 1200px original.
        el('div.o2-aside', null, el('img', {
          src: NS.sized(NS.asset('coach'), 250),
          srcset: NS.sized(NS.asset('coach'), 250) + ' 250w, ' + NS.sized(NS.asset('coach'), 500) + ' 500w',
          sizes: '(min-width: 768px) 180px, 126px',
          alt: '', decoding: 'async',
        })),
        list,
      ]);
      list.classList.add('o2-aside-list');
    }

    return screen({
      title: step.title,
      subtitle: step.subtitle || 'Choose all that apply',
      gap: step.contentGap != null ? step.contentGap : 20,
      gapMd: step.contentGapMd,
      content: content,
      footer: (cta = primaryButton('Next step', ctx.next, { radius: 10, disabled: current.length === 0 })),
    });
  }

  /**
   * Frame for a screen's photo. `step.img` gives real artwork, `step.photo` the placeholder
   * brief.
   *
   * `ar` / `arWeb` are the FRAME's ratio, taken from the design — not the file's. They match
   * the artwork on the interstitials, but not everywhere: the sleep screen's frame is 1.75
   * mobile and 2.55 web while its photos are 1.34 and 2.85, so the frame holds the layout
   * and `cover` crops the picture to it.
   */
  function screenPhoto(step, fallbackHeight) {
    var p;
    if (step.img) {
      p = el('div.o2-photo.has-img', null, photoImage(step.img));
      if (step.img.ar) p.style.setProperty('--o2-ar', step.img.ar);
      if (step.img.arWeb) p.style.setProperty('--o2-ar-web', step.img.arWeb);
    } else {
      p = photoSlot(step.photo, fallbackHeight);
    }
    return p;
  }

  /** The tile screens' photo differs from the interstitial's only in its corner radius. */
  function gridPhoto(step) {
    var p = screenPhoto(step, step.photoHeight || 180);
    p.classList.add('o2-grid-photo');
    return p;
  }

  /** B4 and C1–C3: photo above a 2-up grid of centre-stacked cards. */
  function renderGrid(step, ctx) {
    var current = step.get();
    var g = el('div.o2-grid2');
    var nodes = {};

    step.options.forEach(function (o) {
      var selected = current === o.v;
      var ind = el('span.o2-ind');
      if (selected) ind.appendChild(svg(ICON.tick));
      var node = el('button.o2-tile' + (selected ? '.is-on' : ''), {
        type: 'button', 'aria-pressed': selected ? 'true' : 'false',
        onclick: function () {
          var prev = step.get();
          step.set(o.v);
          if (prev && nodes[prev]) setCardSelected(nodes[prev], false);
          setCardSelected(node, true);
          setTimeout(ctx.next, SETTLE_MS);
        },
      }, [el('span.o2-tile-label', { text: o.l }), ind]);
      node._ind = ind;
      nodes[o.v] = node;
      g.appendChild(node);
    });

    return screen({
      title: step.title, gap: 24, gapMd: 32,
      content: [gridPhoto(step), g],
    });
  }

  /** W08 and W09: illustrated cards. Resting fill is SURFACE here, not transparent. */
  function renderImages(step, ctx) {
    var current = step.get();
    var g = el('div.o2-grid2.o2-imgs');
    var nodes = {};

    step.options.forEach(function (o) {
      var selected = current === o.v;
      var ind = el('span.o2-ind');
      if (selected) ind.appendChild(svg(ICON.tick));
      var node = el('button.o2-imgcard' + (selected ? '.is-on' : ''), {
        type: 'button', 'aria-pressed': selected ? 'true' : 'false',
        onclick: function () {
          var prev = step.get();
          step.set(o.v);
          if (prev && nodes[prev]) setCardSelected(nodes[prev], false);
          setCardSelected(node, true);
          setTimeout(ctx.next, SETTLE_MS);
        },
      }, [
        // Rendered ~196px tall on mobile and 210 on web, so 400/600 candidates cover 2x
        // without pulling the 1200px original for a thumbnail.
        el('span.o2-imgbox', null, el('img', {
          src: NS.sized(NS.asset(o.img), 400),
          srcset: NS.sized(NS.asset(o.img), 300) + ' 300w, ' + NS.sized(NS.asset(o.img), 600) + ' 600w',
          sizes: '(min-width: 768px) 25vw, 50vw',
          alt: o.l, decoding: 'async',
        })),
        el('span.o2-imgcard-label', { text: o.l }),
        ind,
      ]);
      node._ind = ind;
      nodes[o.v] = node;
      g.appendChild(node);
    });

    return screen({ title: step.title, gap: 12, gapMd: 28, content: g });
  }

  /** C4: one value split under three headings. The headings are labels, not questions. */
  function renderGrouped(step, ctx) {
    var current = step.get();
    var wrap = el('div');
    var nodes = {};

    step.groups.forEach(function (grp) {
      wrap.appendChild(el('div.o2-group-heading', { text: grp.heading }));
      var list = el('div.o2-list.tight');
      grp.options.forEach(function (o) {
        var node = optionCard(o, {
          selected: current === o.v,
          onClick: function () {
            var prev = step.get();
            step.set(o.v);
            if (prev && nodes[prev]) setCardSelected(nodes[prev], false);
            setCardSelected(node, true);
            setTimeout(ctx.next, SETTLE_MS);
          },
        });
        nodes[o.v] = node;
        list.appendChild(node);
      });
      wrap.appendChild(list);
    });

    return screen({ title: step.title, gap: 24, content: wrap });
  }

  /**
   * Interstitials. Mobile stacks; web is a two-column grid with a 320px button block and
   * a 520px photo — a genuinely different layout, not a reflow.
   *
   * The footnote sits ABOVE the button on mobile and BELOW it on web, consistently across
   * W04, W14 and A10.
   */
  function renderInterstitial(step, ctx) {
    var body = el('p.o2-inter-body' + (step.bodyInk ? '.ink' : ''), { text: resolve(step.body) });
    var photo = null;
    if (step.img || step.photo) {
      photo = screenPhoto(step, null);
      photo.classList.add('o2-inter-photo');
      if (step.photoHeight) photo.style.setProperty('--o2-photo-h', step.photoHeight + 'px');
    }

    var note = step.footnote ? el('p.o2-footnote', { text: step.footnote }) : null;

    /*
     * Title/body, photo and actions are SIBLINGS, not nested. Mobile orders them
     * title → body → photo → spacer → button; web places them in a two-column grid with
     * the actions under the text and the photo spanning both rows. Nesting the actions
     * inside the text block made the button render directly under the body on mobile with
     * the photo pushed below it.
     */
    var actions = el('div.o2-inter-actions', null, [
      // Footnote sits ABOVE the button on mobile and BELOW it on web — consistent across
      // W04, W14 and A10 in both design files.
      note ? el('div.only-mobile', null, note.cloneNode(true)) : null,
      primaryButton(step.cta || 'Continue', ctx.next, { radius: step.secondary ? 12 : 10 }),
      step.secondary ? textButton(step.secondary.label, step.secondary.onClick) : null,
      note ? el('div.only-web', null, note) : null,
    ]);

    /*
     * Text and actions are wrapped together, but the wrapper is `display: contents` on
     * mobile — so its children participate directly in the column and can be ordered
     * title → body → photo → spacer → button. On web it becomes a real grid item holding
     * both, which keeps the button tight under the copy instead of being pushed apart by
     * the 520px photo stretching the rows.
     */
    return el('div.o2-inter', null, [
      el('div.o2-inter-main', null, [
        el('div.o2-inter-text', null, [
          el('h1.o2-title.big', { text: resolve(step.title) }),
          body,
        ]),
        el('div.o2-inter-spacer.only-mobile'),
        actions,
      ]),
      photo,
    ]);
  }

  NS.ui = {
    photoSrcset: photoSrcset,
    el: el, svg: svg, ICON: ICON, resolve: resolve,
    header: header, screen: screen, photoSlot: photoSlot,
    primaryButton: primaryButton, textButton: textButton,
    optionCard: optionCard, setCardSelected: setCardSelected, SETTLE_MS: SETTLE_MS,
  };

  NS.renderers = {
    single: renderSingle,
    multi: renderMulti,
    grid: renderGrid,
    images: renderImages,
    grouped: renderGrouped,
    interstitial: renderInterstitial,
  };
})(window.ReformlyQuiz);
